<template>
    <div class="quiz-answers">
        <div class="quiz-answers__background">
            <img src="~@/assets/images/quiz-bgc.png" alt="kasanowa" />
        </div>
        <div class="quiz-answers__background-yellow kasanowa-desktop">
            <img src="~@/assets/images/yellow-elipse.png" alt="kasanowa" />
        </div>
        <KLayoutWrapper>
            <template #default>
                <div>
                    <div class="quiz-answers__title">{{ $t('quiz.moneyForQuizAnswers') }}</div>
                    <div class="quiz-answers__description">{{ $t('quiz.moneyForQuizAnswersDescription') }} <span>{{ $t('quiz.moneyForQuizAnswersDescription2') }}</span></div>
                    <KLoader v-if="loading" />
                    <div v-if="!loading" class="quiz-answers__answers-wrapper">
                        <img src="~@/assets/images/money.png" alt="kasanowa" class="quiz-answers__img-money" />
                        <div class="quiz-answers__to-win-wrapper">
                            <div class="quiz-answers__to-win-money">{{ moneyToWin }} PLN</div>
                            <div class="quiz-answers__to-win-text">{{ $t('quiz.toWin') }}</div>
                        </div>
                        <div class="quiz-answers__current-questions-wrapper">{{ $t('quiz.answersQuestion') }} {{ currentQuestion }}/{{ totalQuestions }}</div>
                        <div class="quiz-answers__item-question" v-html="questions[currentQuestion - 1].question_text"></div>
                        <div class="quiz-answers__answers-item-wrapper">
                            <div
                                v-for="(answer, index) in questions[currentQuestion - 1].answers"
                                :key="questions[currentQuestion - 1].id"
                                class="quiz-answers__item-answer"
                                @click="selectItem(answer, questions[currentQuestion - 1].answers)"
                                :class="{'quiz-answers__item-answer--selected': answer.is_selected_answer, 'quiz-answers__item-answer--open': questions[currentQuestion - 1].type === QUIZ_TYPE_OPEN}"
                            >
                                <div v-if="questions[currentQuestion - 1].type !== QUIZ_TYPE_OPEN">{{ String.fromCharCode(65 + index) }}. {{ answer.answer_text }}</div>
                                <div v-if="questions[currentQuestion - 1].type === QUIZ_TYPE_OPEN" class="quiz-answers__open-answer">
                                    <div v-if="questions[currentQuestion - 1].type !== QUIZ_TYPE_OPEN">{{ String.fromCharCode(65 + index) }}.</div>
                                    <KTextArea
                                        :id="`answer-quiz-${answer.id}`"
                                        v-model="answer.selected_answer_value"
                                        name="answer"
                                        label=""
                                        hide-details
                                        :placeholder="$t('quiz.typeAnswer')"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="!loading" class="quiz-answers__btn-wrapper">
                        <KButton
                            id="prev-question"
                            :label="$t('quiz.back')"
                            secondary
                            @click="prevQuestion"
                            :disabled="currentQuestion === 1"
                        />
                        <KButton
                            id="next-question"
                            :label="$t('quiz.next')"
                            @click="nextQuestion"
                            :disabled="loading"
                        />
                    </div>
                </div>
            </template>
        </KLayoutWrapper>
    </div>
</template>

<script setup lang="ts">
import { ref } from '@vue/runtime-core';
import router from '@/router';
import { useUserStore } from '@/stores/user';
import { useQuizStore } from '@/stores/quiz';
import QuizApi from '@/api/v1/QuizApi';
import UserApi from '@/api/v1/UserApi';
import { toast } from 'vue3-toastify';
import { navigateToWithErrorToast, navigateToWithSuccessToast } from '@/hooks/NavigateHooks';
import { QUIZ_TYPE_OPEN } from '@/hooks/QuizHooks';
import type {
    IQuizDetailsAnswersResponse,
    IQuizDetailsQuestionsResponse
} from '@/models/QuizModel';
import KLayoutWrapper from '@/components/atoms/KLayoutWrapper.vue';
import KLoader from '@/components/atoms/KLoader.vue';
import KButton from '@/components/atoms/KButton.vue';
import KTextArea from '@/components/atoms/KTextArea.vue';

const { setQuizInfo, setUserAnswers, setQuizSubmitResponse } = useQuizStore();

const moneyToWin = ref<number>(0);
const currentQuestion = ref<number>(1);
const totalQuestions = ref<number>(0);
const questions = ref<IQuizDetailsQuestionsResponse[]>([]);
const loading = ref<boolean>(false);
const quizApi = new QuizApi();
const quizDetails = async() => {
    loading.value = true;
    try {
        const result = await quizApi.quizDetails(router.currentRoute.value.params.id);
        if (!result.isSuccess) {
            navigateToWithErrorToast('/quiz-ph', t('quiz.toast.errorQuizDetails'));
            loading.value = false;
            return;
        }
        moneyToWin.value = +result.payload.value;
        totalQuestions.value = result.payload.questions.length;
        setQuizInfo(result.payload);
        questions.value = result.payload.questions.map((elem: IQuizDetailsQuestionsResponse) => {
            return {
                ...elem,
                answers: elem.answers.map((elems: any) => {
                    return {
                        ...elems,
                        is_selected_answer: false,
                        selected_answer_value: null,
                    };
                }),
            };
        });
    } catch (e) {
        navigateToWithErrorToast('/quiz-ph', t('quiz.toast.errorQuizDetails'))
        loading.value = false;
        return;
    }
    loading.value = false;
};
quizDetails();

const prevQuestion = () => {
    if (currentQuestion.value > 1) {
        currentQuestion.value = currentQuestion.value - 1;
    }
};
const nextQuestion = () => {
    const selectedAnswer: IQuizDetailsAnswersResponse | null = questions.value[currentQuestion.value - 1].answers.find((elem: IQuizDetailsAnswersResponse) => elem.is_selected_answer === true);
    if (selectedAnswer == null) {
        toast.warning(t('quiz.toast.selectAnswerFirst'));
        return;
    }
    if (questions.value[currentQuestion.value - 1].type === QUIZ_TYPE_OPEN && (selectedAnswer.selected_answer_value == null || selectedAnswer.selected_answer_value == '')) {
        toast.warning(t('quiz.toast.typeSomethingInOpenAnswer'));
        return;
    }
    if (currentQuestion.value < totalQuestions.value) {
        currentQuestion.value = currentQuestion.value + 1;
        return;
    }
    if (currentQuestion.value === totalQuestions.value) {
        saveQuizAnswers();
    }
};
const selectItem = (answer: IQuizDetailsAnswersResponse, answers: IQuizDetailsAnswersResponse[]) => {
    answers.forEach((answer: IQuizDetailsAnswersResponse) => {
        answer.is_selected_answer = false;
    });
    if (!answer.is_selected_answer) {
        answer.is_selected_answer = true;
    }
};
const { setUserInfo } = useUserStore();
const userApi = new UserApi();
const user = async() => {
    try {
        const result = await userApi.user();
        if (!result.isSuccess) {
            return;
        }
        await setUserInfo(result.payload);
    } catch (e) {
        return;
    }
};
const saveQuizAnswers = async() => {
    loading.value = true;
    let request: any = {};
    request.answers = questions.value.map((question: IQuizDetailsQuestionsResponse) => {
        return {
            questionId: question.id,
            answerId: question.answers.find(x => x.is_selected_answer === true).id || null,
            answerText: question.answers.find(x => x.is_selected_answer === true).answer_text || null,
        };
    });
    try {
        const result = await quizApi.saveQuizAnswers(
            router.currentRoute.value.params.id,
            request,
        )
        if (!result.isSuccess) {
            toast.error(t('quiz.toast.errorSaveQuizAnswers'));
            loading.value = false;
            return;
        }
        await user();
        await setUserAnswers(questions.value);
        await setQuizSubmitResponse(result.payload);
        navigateToWithSuccessToast('/quiz-ph/summary', t('quiz.toast.successSaveQuizAnswers'));
    } catch (e) {
        toast.error(t('quiz.toast.errorSaveQuizAnswers'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
</script>

<style scoped lang="scss">
.quiz-answers {
    &__background {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        width: 100%;
        height: 100%;

        img {
            object-fit: cover;
            width: 100%;
            height: calc(100% - 184px);
            min-height: calc(100vh - 184px);

            @include media-breakpoint-up(lg) {
                height: calc(100% - 217px);
                min-height: calc(100vh - 217px);
            }
        }
    }

    &__background-yellow {
        position: absolute;
        top: -30px;
        left: -40%;
        z-index: 0;
        width: 100%;
        height: max-content;

        img {
            object-fit: cover;
            width: max-content;
            height: 100vh;
        }
    }

    &__title {
        background-color: $primary-400;
        text-transform: uppercase;
        color: $black;
        font-weight: 900;
        font-size: 32px;
        text-align: center;
        width: max-content;
        display: flex;
        margin: 0 auto;
        padding: 6px 20px;

        @include media-breakpoint-up(lg) {
            font-size: 52px;
            padding: 6px 40px;
        }
    }

    &__description {
        margin-top: 20px;
        color: $white;
        text-align: center;

        span {
            font-weight: 700;
            color: $primary-400;
        }
    }

    &__answers-wrapper {
        position: relative;
        border-radius: 10px;
        border: 1px solid $primary-400;
        background: rgba($primary-400, 0.6);
        backdrop-filter: blur(2px) saturate(150%);
        -webkit-backdrop-filter: blur(5px) saturate(150%);
        padding: 36px 16px;
        margin-top: 190px;

        @include media-breakpoint-up(lg) {
            max-width: 1126px;
            padding: 80px 70px 50px 50px;
            margin: 100px auto 0;
        }
    }

    &__to-win-wrapper {
        position: absolute;
        top: -170px;
        right: 50%;
        transform: translateX(50%);
        width: 100%;
        background-color: $black;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 15px 0;
        border-radius: 5px;

        @include media-breakpoint-up(lg) {
            top: -80px;
            width: max-content;
            padding: 8px 30px;
            transform: none;
            right: 70px;
        }
    }

    &__to-win-money {
        color: $secondary-400;
        font-weight: 900;
        font-size: 52px;
        line-height: 1;

        @include media-breakpoint-up(lg) {
            font-size: 72px;
        }
    }

    &__to-win-text {
        color: $white;
        font-weight: 700;
        font-size: 24px;

        @include media-breakpoint-up(lg) {
            font-size: 42px;
        }
    }

    &__current-questions-wrapper {
        position: absolute;
        top: -23px;
        left: 50%;
        transform: translateX(-50%);
        background-color: $secondary-400;
        color: $black;
        font-size: 18px;
        padding: 7px 20px;
        font-weight: 700;
        width: max-content;
        text-transform: uppercase;

        @include media-breakpoint-up(lg) {
            font-size: 32px;
            transform: none;
            left: 50px;
            top: -32px;
        }
    }

    &__item-question {
        color: $white;
        text-align: center;
        font-size: 16px;
        margin-bottom: 15px;

        @include media-breakpoint-up(lg) {
            font-size: 32px;
        }
    }

    &__answers-item-wrapper {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;

        @include media-breakpoint-up(lg) {
            justify-content: space-between;
            gap: 40px;
        }
    }

    &__btn-wrapper {
        display: flex;
        margin-top: 10px;
        justify-content: space-between;
        margin-bottom: 200px;

        @include media-breakpoint-up(lg) {
            max-width: 1126px;
            width: 100%;
            margin: 20px auto 160px;
            justify-content: flex-end;
            gap: 90px;
            padding-right: 70px;
        }
    }

    &__img-money {
        position: absolute;
        bottom: -230px;
        left: 100%;
        width: 250px;
        height: 166px;
        transform: translateX(-100%);

        @include media-breakpoint-up(lg) {
            width: 352px;
            height: 250px;
            transform: none;
            left: 50px;
            bottom: -210px;
        }
    }

    &__item-answer {
        background-color: $primary-900;
        width: 100%;
        font-size: 16px;
        word-break: break-word;
        color: $white;
        padding: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        cursor: pointer;
        transition: background-color 0.3s, color 0.3s;

        @include media-breakpoint-up(lg) {
            font-size: 24px;
            width: 40%;
        }

        &:hover {
            color: $primary-900;
            background-color: $primary-400;
            font-weight: 700;
        }

        &--selected {
            color: $primary-900;
            background-color: $primary-400;
            font-weight: 700;
        }

        &--open {
            width: 100%;
            padding: 0;

            &:deep(.k-text-area__errors) {
                display: none;
            }
        }
    }

    &__open-answer {
        width: 100%;

        &:deep(.k-text-area) {
            width: 100%;
            background-color: $white;
        }
    }
}
</style>
